import { IVector3, CardinalDirection, Vector3, IBoundingBox, RenderLayerMask } from '@inter-ikea-betrakta/viewer';
import { OrthographicPlannerCamera } from '@inter-ikea-betrakta/viewer/dist/modules/cameras/OrthographicPlannerCamera';
import { ComponentIds, ComponentUtils, HierarchyUtils, MeasurementUtils, RoomConnectorConstants, Transform, TransformUtils, Vector, VectorConstants } from '@inter-ikea-core/core';
import { AssemblyInstructionMeasurementPosition } from '../types';
import * as R from 'ramda';
import OuterMeasurementsComponent from './components/OuterMeasurementsComponent';
import { getCardinalDirectionForWall } from '../utils/wallPosition';
/**
 * This is a class to do about the same functionality as we had with the old screenshot tool's function "create_screenshot_for_assembly_instructions".
 * Please note that you want an object of this class to be persistent, as it will "leak" render layers, eventually causing it to stop working.
 * Thus, you should only ever create on instance of this class, and use it everywhere in your codebase. Will look at fixing this though (IC3V-1801)
 */
export class AssemblyScreenshotHelper {
    constructor(viewer, render_layer_allocation) {
        this.viewer = viewer;
        /**
         * Returns the center position of the entities in the image so we can place the
         * number for each entity in the instructions.
         */
        this.getEntityPointsData = (entities, entityIdMap, camera, width, height) => {
            const entitiesWithPosition = entities.map(entity => {
                const worldTransform = TransformUtils.worldTransform(entity);
                return { entity, position: worldTransform.position };
            });
            return entitiesWithPosition
                .map(e => {
                return {
                    entity: e.entity,
                    instructionNbr: entityIdMap.find(i => i.entity.id === e.entity.id)?.instructionNbr,
                    coord: camera.project_world_position(new Vector3(e.position.X / 1000, e.position.Y / 1000, e.position.Z / 1000), width, height)
                };
            })
                .filter(e => e.coord !== null);
        };
        /**
         * Returns the measurement points for the given entities and position.
         * The points are projected to the screen coordinates using the camera.
         */
        this.getMeasurementPoints = (entities, position, camera, width, height) => {
            return entities.flatMap(entity => {
                const points = [];
                const allEntities = HierarchyUtils.getHierarchyWithComponent(entity, OuterMeasurementsComponent.id);
                const worldTransform = TransformUtils.worldTransform(entity);
                // We concatenate the world transform rotation with the camera rotation to get the measurements
                // relative to the camera position. This is needed for the corner frame.
                const transformRelativeCamera = worldTransform.concatenate(new Transform(VectorConstants.ZERO, worldTransform.forward.getRotationTo(new Vector(camera.backward.x, camera.backward.y, camera.backward.z))));
                const localMeasurements = MeasurementUtils.getLocalOutsideMeasurementForEntities(allEntities, transformRelativeCamera);
                if (!localMeasurements) {
                    return [];
                }
                // Get the min value for measurement. If AssemblyInstructionMeasurementPosition is LEFT or RIGHT
                // we use the Y value, otherwise we use the X value.
                const worldPositionStart = position === AssemblyInstructionMeasurementPosition.BOTTOM
                    ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.min.X, localMeasurements.min.Y, localMeasurements.max.Z))
                    : position === AssemblyInstructionMeasurementPosition.TOP
                        ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.min.X, localMeasurements.max.Y, localMeasurements.max.Z))
                        : position === AssemblyInstructionMeasurementPosition.LEFT
                            ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.min.X, localMeasurements.min.Y, localMeasurements.max.Z))
                            : transformRelativeCamera.transformPosition(new Vector(localMeasurements.max.X, localMeasurements.min.Y, localMeasurements.max.Z));
                const startPoint = camera.project_world_position(new Vector3(worldPositionStart.X / 1000, worldPositionStart.Y / 1000, worldPositionStart.Z / 1000), width, height);
                if (startPoint) {
                    points.push({
                        coord: startPoint,
                        positionAlongWall: position === AssemblyInstructionMeasurementPosition.RIGHT ||
                            position === AssemblyInstructionMeasurementPosition.LEFT
                            ? worldPositionStart.rotate(transformRelativeCamera.inverse().rotation).Y
                            : worldPositionStart.rotate(transformRelativeCamera.inverse().rotation).X
                    });
                }
                // Get the max value for measurement. If AssemblyInstructionMeasurementPosition is LEFT or RIGHT
                // we use the Y value, otherwise we use the X value.
                const worldPositionEnd = position === AssemblyInstructionMeasurementPosition.BOTTOM
                    ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.max.X, localMeasurements.min.Y, localMeasurements.max.Z))
                    : position === AssemblyInstructionMeasurementPosition.TOP
                        ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.max.X, localMeasurements.max.Y, localMeasurements.max.Z))
                        : position === AssemblyInstructionMeasurementPosition.LEFT
                            ? transformRelativeCamera.transformPosition(new Vector(localMeasurements.min.X, localMeasurements.max.Y, localMeasurements.max.Z))
                            : transformRelativeCamera.transformPosition(new Vector(localMeasurements.max.X, localMeasurements.max.Y, localMeasurements.max.Z));
                const endPoint = camera.project_world_position(new Vector3(worldPositionEnd.X / 1000, worldPositionEnd.Y / 1000, worldPositionEnd.Z / 1000), width, height);
                if (endPoint) {
                    points.push({
                        coord: endPoint,
                        positionAlongWall: position === AssemblyInstructionMeasurementPosition.RIGHT ||
                            position === AssemblyInstructionMeasurementPosition.LEFT
                            ? worldPositionEnd.rotate(transformRelativeCamera.inverse().rotation).Y
                            : worldPositionEnd.rotate(transformRelativeCamera.inverse().rotation).X
                    });
                }
                return points;
            });
        };
        const advanced_viewer = viewer;
        this._isolation_render_layer = advanced_viewer.create_render_layer(render_layer_allocation);
    }
    /**
     * Takes a screenshot of the objects provided, and projects points in the world to coordinates on the screenshot.
     * @param objects The objects to show in the screenshot and frame
     * @param world_direction The direction used for framing
     * @param worldspace_coords The points in the world you want to project to coordinates on the screenshot
     * @param width The width of the screenshot
     * @param height The height of the screenshot
     * @param settings The settings used to take the screenshot
     * @param margin The viewport margin (guaranteed space around the objects) in normalized coordinates. Should be in the range 0 - 0.499
     * @param precise If set to true, the framing will be precise (can be significantly more expensive to compute)
     * @returns A promise which will contain the image representing the screenshot, as well as a list of points which are the projected positions of 'worldspace_coords'.
     * The indices of 'worldspace_coords' and the points returned will match. If a given point was outside of the screenshot, that specific point will be 'null' in the returned points.
     */
    async create_screenshot_for_assembly_instructions(objects, world_direction, worldspace_coords, width, height, settings, margin, precise, camera) {
        // Add the objects to our isolation layer
        for (let i = 0; i < objects.length; i++) {
            this.viewer.add_to_layer(objects[i], this._isolation_render_layer);
        }
        // Make sure camera has correct state
        await camera.update_aspect_ratio(width / height);
        // Frame camera
        await camera.frame_objects(objects, precise, margin, world_direction).catch(e => {
            // eslint-disable-next-line no-console
            console.error('Failed to frame objects', e);
        });
        // Take screenshot and wait for it to finish
        const image = await camera.take_screenshot(height, settings, new RenderLayerMask([this._isolation_render_layer]));
        // Restore objects
        for (let i = 0; i < objects.length; i++) {
            this.viewer.remove_from_layer(objects[i], this._isolation_render_layer);
        }
        const screen_points = [];
        for (let i = 0; i < worldspace_coords.length; i++) {
            const point = camera.project_world_position(worldspace_coords[i], width, height);
            screen_points.push(point);
        }
        return {
            image: image,
            points: screen_points.filter(p => p !== null)
        };
    }
    async take_edge_sketch_screenshot(entityWallList, entityIdMap, direction, width, height, multiwallSolution) {
        const camera = new OrthographicPlannerCamera(direction, { x: 0, y: 0 }, this.viewer);
        const settings = {
            ao_settings: { enabled: false }
        };
        const objects = entityWallList.flatMap(f => f.entityObjectIds);
        // Add the objects to our isolation layer
        for (let i = 0; i < objects.length; i++) {
            this.viewer.add_to_layer(objects[i], this._isolation_render_layer);
        }
        // Make sure camera has correct state
        await camera.update_aspect_ratio(width / height);
        // Frame objects with a margin
        const bounds = await this.viewer.get_object_bounds_world(...objects);
        const boundsWithMargin = new IBoundingBox(new IVector3(bounds.min.x - 0.5, bounds.min.y - 0.5, bounds.min.z - 0.5), new IVector3(bounds.max.x + 0.5, bounds.max.y + 0.5, bounds.max.z + 0.5));
        camera['_scene_bounds'] = boundsWithMargin;
        camera.frame_precise(boundsWithMargin);
        const image = await this
            .viewer.take_edge_sketch_screenshot(camera.get_projection_matrix(), camera.projection.get_camera_mode(), camera.view_matrix, width, height, settings, new RenderLayerMask([this._isolation_render_layer]));
        // Restore objects
        for (let i = 0; i < objects.length; i++) {
            this.viewer.remove_from_layer(objects[i], this._isolation_render_layer);
        }
        if (direction === CardinalDirection.TOP) {
            // If image is top view, we need to get the measurement points for each wall and display
            // them in the correct position
            const mapByWall = entityWallList.reduce((acc, e) => {
                e.connectedToWallEntities.forEach(wallEntity => {
                    const id = ComponentUtils.displayName(wallEntity) || '';
                    if (!acc[id]) {
                        acc[id] = { wallEntity: wallEntity, entitiesWithPosition: [] };
                    }
                    acc[id].entitiesWithPosition.push(e);
                });
                return acc;
            }, {});
            const measurementPointsData = {};
            for (const wallId of Object.keys(mapByWall)) {
                const direction = getCardinalDirectionForWall(mapByWall[wallId].wallEntity);
                const position = direction === CardinalDirection.LEFT
                    ? AssemblyInstructionMeasurementPosition.RIGHT
                    : direction === CardinalDirection.RIGHT
                        ? AssemblyInstructionMeasurementPosition.LEFT
                        : direction === CardinalDirection.FRONT
                            ? AssemblyInstructionMeasurementPosition.TOP
                            : AssemblyInstructionMeasurementPosition.BOTTOM;
                const entitiesOnWall = mapByWall[wallId].entitiesWithPosition.map(e => e.entity);
                const points = this.getMeasurementPoints([...entitiesOnWall], position, camera, width, height);
                measurementPointsData[position] = R.sortBy(R.prop('positionAlongWall'))(R.uniqBy(R.prop('positionAlongWall'), [...points]));
            }
            camera.dispose();
            return {
                title: 'storageone.kitchenDrawingTopViewTitle',
                image,
                measurementPointsData,
                entityPointsData: this.getEntityPointsData(entityWallList.map(e => e.entity), entityIdMap, camera, width, height)
            };
        }
        else {
            // Image is a front view for one of the walls. We separate the hanging and standing entities
            // and display the width measurements for the standing entities in the bottom and the width
            // measurements for the hanging entities in the top. The height measurements are displayed
            // on the right side of the image.
            const allEntities = entityWallList.map(e => e.entity);
            const { standingEntities, hangingEntities } = entityWallList.reduce((acc, curr) => {
                const connection = curr.entity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.highestPriorityConnection;
                if (connection && RoomConnectorConstants.hasFloorPlacement(connection)) {
                    acc.standingEntities.push(curr.entity);
                }
                else {
                    acc.hangingEntities.push(curr.entity);
                }
                return acc;
            }, { standingEntities: [], hangingEntities: [] });
            const widthPointsAllEntities = this.getMeasurementPoints(allEntities, AssemblyInstructionMeasurementPosition.BOTTOM, camera, width, height);
            const sortedWidthPointsAllEntities = R.sortBy(R.prop('positionAlongWall'))(widthPointsAllEntities);
            const widthEndPoints = [
                sortedWidthPointsAllEntities[0],
                sortedWidthPointsAllEntities[sortedWidthPointsAllEntities.length - 1]
            ];
            const widthPointsStandingEntities = this.getMeasurementPoints(standingEntities, AssemblyInstructionMeasurementPosition.BOTTOM, camera, width, height);
            const widthPointsHangingEntities = this.getMeasurementPoints(hangingEntities, AssemblyInstructionMeasurementPosition.TOP, camera, width, height);
            const heightPoints = this.getMeasurementPoints(allEntities, AssemblyInstructionMeasurementPosition.RIGHT, camera, width, height);
            camera.dispose();
            const title = !multiwallSolution
                ? 'storageone.kitchenDrawingFrontViewTitle'
                : direction === CardinalDirection.LEFT
                    ? 'storageone.kitchenDrawingRightWallTitle"'
                    : direction === CardinalDirection.RIGHT
                        ? 'storageone.kitchenDrawingLeftWallTitle'
                        : direction === CardinalDirection.FRONT
                            ? 'storageone.kitchenDrawingFrontWallTitle'
                            : 'storageone.kitchenDrawingBackWallTitle';
            return {
                title,
                image: image,
                measurementPointsData: {
                    [AssemblyInstructionMeasurementPosition.BOTTOM]: R.sortBy(R.prop('positionAlongWall'))(R.uniqBy(R.prop('positionAlongWall'), [
                        ...widthPointsStandingEntities,
                        ...widthEndPoints
                    ])),
                    [AssemblyInstructionMeasurementPosition.TOP]: R.sortBy(R.prop('positionAlongWall'))(R.uniqBy(R.prop('positionAlongWall'), [
                        ...widthPointsHangingEntities,
                        ...widthEndPoints
                    ])),
                    [AssemblyInstructionMeasurementPosition.RIGHT]: R.sortBy(R.prop('positionAlongWall'))(R.uniqBy(R.prop('positionAlongWall'), [...heightPoints]))
                },
                entityPointsData: this.getEntityPointsData(allEntities, entityIdMap, camera, width, height)
            };
        }
    }
}
//# sourceMappingURL=screenshotHelper.js.map